/**
 * Chart → detail drilldown (#114 / S3).
 * Reuses #113 selection hash keys: well / month / scenario / concern.
 */

import { loadDeskKnobsForNav } from '../engine/deskPersistence.js';
import {
  hashWithContext,
  parseSelectionContext,
} from './selectionContext.js';
import { wellHashId } from './wellIdentity.js';

function clean(value) {
  if (value == null) return '';
  return String(value).trim();
}

function hasOwn(obj, key) {
  return obj != null && Object.prototype.hasOwnProperty.call(obj, key);
}

/** True when hash/route selection should open the detail drawer. */
export function hasActiveDrill(selection = {}) {
  return Boolean(
    clean(selection.selectedWellId) ||
      clean(selection.selectedMonth) ||
      clean(selection.scenarioId),
  );
}

/** Normalize a well chart/table/map mark into selection fields. */
export function selectionFromWellMark(wellOrRow) {
  if (wellOrRow == null) return { selectedWellId: '', selectedMonth: '' };
  if (typeof wellOrRow === 'string' || typeof wellOrRow === 'number') {
    return { selectedWellId: clean(wellOrRow), selectedMonth: '' };
  }
  const id =
    wellHashId(wellOrRow) ||
    clean(wellOrRow.lease) ||
    clean(wellOrRow.well) ||
    clean(wellOrRow.id);
  return { selectedWellId: id, selectedMonth: '' };
}

/**
 * Typed period tokens live in the #113 `month` hash key without colliding kinds:
 * - plain `2025-11` / `2027` → cash-flow / projected CF period
 * - `disc:10` → Reserves PV-sensitivity discount rate (%)
 * - `hh:2026` → Reserves NYMEX price-deck year
 */
export const PERIOD_KIND = {
  month: 'month',
  disc: 'disc',
  hh: 'hh',
};

/** Encode a typed period token for the `month` query key. */
export function encodePeriodToken(kind, value) {
  const v = clean(value);
  if (!v) return '';
  const k = clean(kind).toLowerCase() || PERIOD_KIND.month;
  if (k === PERIOD_KIND.month) return v;
  return `${k}:${v}`;
}

/** Parse a `month` query value into { kind, value }. */
export function parsePeriodToken(token) {
  const s = clean(token);
  if (!s) return { kind: PERIOD_KIND.month, value: '' };
  const m = /^(disc|hh):(.+)$/i.exec(s);
  if (m) return { kind: m[1].toLowerCase(), value: clean(m[2]) };
  return { kind: PERIOD_KIND.month, value: s };
}

/** Normalize a month / period mark (Cash Flow bars, Executive year stacks). */
export function selectionFromMonthMark(rowOrMonth, monthKey = 'month') {
  if (rowOrMonth == null) return { selectedMonth: '', selectedWellId: '' };
  if (typeof rowOrMonth === 'string' || typeof rowOrMonth === 'number') {
    return { selectedMonth: encodePeriodToken(PERIOD_KIND.month, rowOrMonth), selectedWellId: '' };
  }
  const month =
    clean(rowOrMonth[monthKey]) ||
    clean(rowOrMonth.month) ||
    clean(rowOrMonth.year) ||
    clean(rowOrMonth.period) ||
    clean(rowOrMonth.x);
  return { selectedMonth: encodePeriodToken(PERIOD_KIND.month, month), selectedWellId: '' };
}

/** Reserves PV-sensitivity point → typed `disc:<rate>` token (not a cash-flow month). */
export function selectionFromDiscRateMark(rowOrRate) {
  if (rowOrRate == null) return { selectedMonth: '', selectedWellId: '' };
  const rate =
    typeof rowOrRate === 'object' ? rowOrRate.rate ?? rowOrRate.value ?? rowOrRate.x : rowOrRate;
  return {
    selectedMonth: encodePeriodToken(PERIOD_KIND.disc, rate),
    selectedWellId: '',
  };
}

/** Reserves NYMEX price-deck bar → typed `hh:<year>` token. */
export function selectionFromPriceDeckMark(rowOrYear) {
  if (rowOrYear == null) return { selectedMonth: '', selectedWellId: '' };
  const year =
    typeof rowOrYear === 'object' ? rowOrYear.year ?? rowOrYear.period ?? rowOrYear.x : rowOrYear;
  return {
    selectedMonth: encodePeriodToken(PERIOD_KIND.hh, year),
    selectedWellId: '',
  };
}

/**
 * Resolve a period token against page JSON into a drawer payload.
 * Returns null metrics when the series/row is absent — never invents numbers.
 */
export function resolvePeriodDrill(token, pageData = null) {
  const { kind, value } = parsePeriodToken(token);
  const empty = {
    kind,
    value,
    token: clean(token),
    title: '',
    subtitle: '',
    row: null,
    metrics: [],
    empty: true,
  };
  if (!value) return empty;

  if (kind === PERIOD_KIND.disc) {
    const row =
      (pageData?.pvSensitivity || []).find((r) => String(r.rate) === String(value)) || null;
    const metrics = [];
    if (row) {
      metrics.push({ label: 'Discount rate', value: `${Number(row.rate)}%` });
      if (row.pdpMM != null) metrics.push({ label: 'PDP PV', value: `$${Number(row.pdpMM).toFixed(2)}MM` });
      if (row.totalMM != null) {
        metrics.push({ label: 'Total PV', value: `$${Number(row.totalMM).toFixed(2)}MM` });
      }
    }
    return {
      kind,
      value,
      token: encodePeriodToken(PERIOD_KIND.disc, value),
      title: `Discount ${value}%`,
      subtitle: 'PV sensitivity by discount rate · Reserves',
      row,
      metrics,
      empty: !row || metrics.length === 0,
    };
  }

  if (kind === PERIOD_KIND.hh) {
    const row = (pageData?.priceDeck || []).find((r) => String(r.year) === String(value)) || null;
    const metrics = [];
    const oilStrip = row && row.wti != null && row.hh == null;
    if (row) {
      metrics.push({ label: 'Strip year', value: String(row.year) });
      if (row.wti != null) metrics.push({ label: 'WTI', value: `$${Number(row.wti).toFixed(2)}/bbl` });
      if (row.hh != null) metrics.push({ label: 'Henry Hub', value: `$${Number(row.hh).toFixed(2)}/Mcf` });
    }
    return {
      kind,
      value,
      token: encodePeriodToken(PERIOD_KIND.hh, value),
      title: oilStrip ? `WTI strip ${value}` : `HH strip ${value}`,
      subtitle: oilStrip ? 'WTI strip price deck · Reserves' : 'NYMEX strip price deck · Reserves',
      row,
      metrics,
      empty: !row || metrics.length === 0,
    };
  }

  const months = pageData?.months || [];
  const row =
    months.find((m) => String(m.month) === String(value) || String(m.year) === String(value)) ||
    (pageData?.projectedCf || []).find((m) => String(m.year) === String(value)) ||
    null;
  const metrics = [];
  if (row) {
    if (row.nocf != null) metrics.push({ label: 'NOCF', value: row.nocf, format: 'currency' });
    if (row.realizedPrice != null) {
      metrics.push({ label: 'Realized', value: `$${Number(row.realizedPrice).toFixed(2)}/Mcf` });
    }
    if (row.gasVolumeMcf != null) {
      metrics.push({ label: 'Volume', value: row.gasVolumeMcf, format: 'int' });
    }
    if (row.loe != null) metrics.push({ label: 'LOE', value: row.loe, format: 'currency' });
    if (row.total != null) {
      metrics.push({ label: 'Projected CF', value: `$${Number(row.total).toFixed(1)}M` });
    } else if (row.pdp != null) {
      metrics.push({ label: 'Projected CF', value: `PDP $${Number(row.pdp || 0).toFixed(1)}M` });
    }
  }
  return {
    kind: PERIOD_KIND.month,
    value,
    token: encodePeriodToken(PERIOD_KIND.month, value),
    title: `Period ${value}`,
    subtitle: 'Cash-flow / projection mark from the active summary chart',
    row,
    metrics,
    empty: !row || metrics.length === 0,
  };
}

/** Normalize a named scenario mark (Decision scenario table). */
export function selectionFromScenarioMark(rowOrId) {
  if (rowOrId == null) return { scenarioId: '' };
  if (typeof rowOrId === 'string' || typeof rowOrId === 'number') {
    return { scenarioId: clean(rowOrId) };
  }
  return {
    scenarioId: clean(rowOrId.id) || clean(rowOrId.scenarioId) || clean(rowOrId.scenario),
  };
}

/**
 * Merge partial selection onto current hash selection and (optionally) apply.
 * @returns {string} hash path+query (no leading #)
 */
export function openDrillSelection(partial = {}, opts = {}) {
  const current = parseSelectionContext(opts.hash);
  const path = clean(opts.path || current.path || 'overview').replace(/^#/, '') || 'overview';
  const selection = {
    selectedWellId: hasOwn(partial, 'selectedWellId')
      ? clean(partial.selectedWellId)
      : current.selectedWellId,
    selectedMonth: hasOwn(partial, 'selectedMonth')
      ? clean(partial.selectedMonth)
      : current.selectedMonth,
    scenarioId: hasOwn(partial, 'scenarioId')
      ? clean(partial.scenarioId)
      : current.scenarioId,
    concernId: hasOwn(partial, 'concernId')
      ? clean(partial.concernId)
      : current.concernId,
  };
  const knobs =
    opts.knobs ||
    (typeof window !== 'undefined' ? loadDeskKnobsForNav() : {});
  const hash = hashWithContext(path, {
    selection,
    knobs,
    preserveExisting: opts.preserveExisting !== false,
  });
  if (typeof window !== 'undefined' && opts.apply !== false) {
    window.location.hash = hash;
  }
  return hash;
}

/** Clear well/month/scenario drill keys; optionally clear concern too. */
export function clearDrillSelection(opts = {}) {
  const partial = {
    selectedWellId: '',
    selectedMonth: '',
    scenarioId: '',
  };
  if (opts.clearConcern) partial.concernId = '';
  return openDrillSelection(partial, opts);
}

/**
 * Provenance block for the drawer — package-level + selection keys.
 * Never invents sources; empty fields stay null.
 */
export function buildDrillProvenance({
  dealPackage = null,
  pageData = null,
  selection = {},
  surface = '',
} = {}) {
  const pkgProv = dealPackage?.provenance || {};
  const meta = dealPackage?.meta || pageData?.meta || {};
  return {
    asOf: pkgProv.asOf || meta.generatedAt || meta.imDate || null,
    generatorVersion: pkgProv.generatorVersion || meta.generatorVersion || null,
    duckdb: pkgProv.duckdb || null,
    vdrSource: pkgProv.vdrSource || null,
    priceDeck: meta.priceDeck || null,
    dealId: meta.dealId || null,
    streamMethod: meta.streamMethod || null,
    surface: clean(surface) || null,
    selectedWellId: clean(selection.selectedWellId) || null,
    selectedMonth: clean(selection.selectedMonth) || null,
    scenarioId: clean(selection.scenarioId) || null,
    concernId: clean(selection.concernId) || null,
  };
}

/**
 * Slim drill snapshot for assistant context (`livePageState.drill` seam).
 */
export function slimDrillForAssistant(selection = {}, provenance = null) {
  if (!hasActiveDrill(selection) && !clean(selection.concernId)) return null;
  return {
    selectedWellId: clean(selection.selectedWellId) || null,
    selectedMonth: clean(selection.selectedMonth) || null,
    scenarioId: clean(selection.scenarioId) || null,
    concernId: clean(selection.concernId) || null,
    provenance: provenance
      ? {
          asOf: provenance.asOf || null,
          dealId: provenance.dealId || null,
          generatorVersion: provenance.generatorVersion || null,
          surface: provenance.surface || null,
        }
      : null,
  };
}
